import { z } from 'zod';

/**
 * Cross-platform browser capability model.
 *
 * Replaces the v1 single-`browser` field on a run with a structured
 * `(engine, browser, browserVersion, os, osVersion, device,
 * viewport)` tuple. The runner reads this verbatim — it never
 * second-guesses what to launch.
 *
 * `engine` is the rendering engine; `browser` is the brand. Both are
 * stored explicitly so we can tell Edge (chromium) apart from Chrome
 * (chromium) at the billing layer without re-deriving.
 */

// ---- Atoms ----

export const BROWSER_ENGINES = ['chromium', 'gecko', 'webkit'] as const;
export type BrowserEngine = (typeof BROWSER_ENGINES)[number];
export const BrowserEngineSchema = z.enum(BROWSER_ENGINES);

export const BROWSER_BRANDS = ['chrome', 'edge', 'firefox', 'safari', 'webkit'] as const;
export type BrowserBrand = (typeof BROWSER_BRANDS)[number];
export const BrowserBrandSchema = z.enum(BROWSER_BRANDS);

export const BROWSER_OS_LIST = ['windows', 'macos', 'linux', 'ios', 'android'] as const;
export type BrowserOS = (typeof BROWSER_OS_LIST)[number];
export const BrowserOSSchema = z.enum(BROWSER_OS_LIST);

/**
 * Provider id for the layer that physically owns the browser session.
 * Mirrors `CloudBrowserProviderSchema` but adds `cdp-local` because
 * the cloud-browser admin config doesn't list local mode as a
 * "provider" — that level lives behind the runner.
 */
export const BROWSER_PROVIDER_IDS = ['cdp-local', 'steel', 'browserstack', 'saucelabs', 'lambdatest'] as const;
export type BrowserProviderId = (typeof BROWSER_PROVIDER_IDS)[number];
export const BrowserProviderIdSchema = z.enum(BROWSER_PROVIDER_IDS);

/**
 * Browser version selector. Concrete versions (e.g. `'124'`) and
 * floating tags (`'latest'`, `'latest-1'`, `'latest-2'`) are both
 * valid. Cloud providers normalise the floating tags themselves;
 * local execution always uses whatever Playwright bundles.
 */
export const BrowserVersionSchema = z
  .string()
  .min(1)
  .max(32)
  .regex(
    /^(latest(-[0-9]+)?|[0-9]+(\.[0-9]+){0,3})$/,
    "browserVersion must be 'latest', 'latest-N', or a numeric version",
  );
export type BrowserVersion = z.infer<typeof BrowserVersionSchema>;

export const ViewportSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type Viewport = z.infer<typeof ViewportSchema>;

// ---- Capability tuple ----

/**
 * The canonical `(engine, browser, os, ...)` tuple. Persisted on
 * test_runs.capability and on the wire whenever a request needs to
 * pin where it should run.
 *
 * Defaults policy:
 *   - `engine` is required (the runner must know which Playwright
 *     binary to load).
 *   - `browser` is required (drives the user-agent + version policy).
 *   - `os` is required (drives provider routing — the same browser on
 *     Windows vs macOS routes to different providers).
 *   - Everything else is optional. When omitted the resolver falls
 *     through to the provider's default.
 */
export const ExecutionCapabilitySchema = z.object({
  engine: BrowserEngineSchema,
  browser: BrowserBrandSchema,
  browserVersion: BrowserVersionSchema.optional(),
  os: BrowserOSSchema,
  osVersion: z.string().min(1).max(32).optional(),
  /** Mobile device emulation/profile (e.g. 'iPhone 15', 'Pixel 8'). null for desktop. */
  device: z.string().min(1).max(64).optional(),
  viewport: ViewportSchema.optional(),
});
export type ExecutionCapability = z.infer<typeof ExecutionCapabilitySchema>;

// ---- Capability resolution surface ----

/**
 * Why a provider was rejected by the resolver. Surfaced verbatim in
 * `RynoraError.detail.reasons[providerId]` so the picker UI can
 * render a precise tooltip on greyed-out chips.
 */
export const ProviderRejectionReasonSchema = z.enum([
  'provider_disabled', // admin_config.cloudBrowser.enabled = false or specific provider creds missing
  'provider_not_implemented', // adapter not in IMPLEMENTED_CLOUD_BROWSER_PROVIDERS yet
  'capability_not_supported', // adapter's supports() returned false for this tuple
  'host_os_mismatch', // local provider on Linux can't satisfy a macOS-Safari request
  'plan_not_entitled', // cross-browser SKU not on this org's plan
  'quota_exceeded', // org has consumed its monthly minutes
]);
export type ProviderRejectionReason = z.infer<typeof ProviderRejectionReasonSchema>;

/**
 * One row of the capability picker matrix. Returned by
 * `GET /api/runs/capabilities` so the UI can render available chips
 * without doing N round-trips.
 */
export const ProviderCapabilityRowSchema = z.object({
  provider: BrowserProviderIdSchema,
  enabled: z.boolean(),
  /**
   * Capabilities this provider currently supports for the active
   * org/plan/admin-config. Empty when `enabled = false`.
   */
  supports: z.array(ExecutionCapabilitySchema),
  /**
   * Per-tuple reasons indexed by a stable JSON key for the capability.
   * Empty when the picker shouldn't surface "why not" — purely
   * cosmetic for greyed-out cells.
   */
  rejections: z.record(ProviderRejectionReasonSchema).optional(),
});
export type ProviderCapabilityRow = z.infer<typeof ProviderCapabilityRowSchema>;

export const ListCapabilitiesResponseSchema = z.object({
  /** OS the API server is running on; the local provider is anchored to this. */
  hostOs: BrowserOSSchema,
  providers: z.array(ProviderCapabilityRowSchema),
  /**
   * The default capability the UI should pre-select when the user
   * hasn't overridden it. Today this is `(chromium, chrome, hostOs)`.
   */
  defaultCapability: ExecutionCapabilitySchema,
});
export type ListCapabilitiesResponse = z.infer<typeof ListCapabilitiesResponseSchema>;

// ---- Helpers ----

/**
 * Stable JSON key for a capability tuple. Used as the key in the
 * `rejections` map and as the value the picker UI compares chips by.
 *
 * Format: `<engine>:<browser>:<os>[:<device>]`. Versions are not part
 * of the key on purpose — picking "Chrome 124" vs "Chrome latest" is
 * the same cell in the matrix; the version is a sub-control.
 */
export function capabilityKey(cap: Pick<ExecutionCapability, 'engine' | 'browser' | 'os' | 'device'>): string {
  const parts: string[] = [cap.engine, cap.browser, cap.os];
  if (cap.device) parts.push(cap.device);
  return parts.join(':');
}

/**
 * Derive `engine` from `browser`. Edge and Chrome both map to
 * Chromium; Safari to WebKit; Firefox to Gecko. Surface used by the
 * UI when the user only picks a brand.
 */
export function engineForBrand(brand: BrowserBrand): BrowserEngine {
  switch (brand) {
    case 'chrome':
    case 'edge':
      return 'chromium';
    case 'firefox':
      return 'gecko';
    case 'safari':
    case 'webkit':
      return 'webkit';
  }
}
